import React, { useEffect, useState } from 'react';

import { Box } from '@material-ui/core';

import { PageLoading } from '@vyce/core/src/components';
import { SettingsPage, SettingsSectionConfig as Section } from '@vyce/core/src/components/SettingsCard';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { CustomDetailsFieldsModule, ExportingTimeLogsModule } from '@vyce/core/src/modules';

import {
  AutomaticClockOut,
  DormantTeamMembers,
  TimeLogsReporting,
  ZeroHoursForClockOuts,
} from './components';
import { useSettingsData } from './hooks';
import { TimeRules } from '../TimeRules';
import config from '../../../assets/config';
import { MANAGEMENT_PERMISSIONS, TimeAndAttendanceLocation } from '../../../types';
import { TimeNotifications } from '../TimeNotifications';
import { getCompanySettingsRequest, updateAutoUserDeletionRequest } from '../../../api/companies';

interface Props {
  companyId?: string;
  locations?: TimeAndAttendanceLocation[];
  getCompanySettings?: typeof getCompanySettingsRequest;
  updateAutoUserDeletion?: typeof updateAutoUserDeletionRequest;
}

export const TimeSettings: React.FC<Props> = ({
  companyId,
  locations,
  getCompanySettings,
  updateAutoUserDeletion,
}) => {
  const { can } = usePermissions();
  const [query, setQuery] = useState<string>('');
  const [openCards, setOpenCards] = useState<Record<string, boolean>>({});

  const {
    settings,
    endDay,
    countAutoClockOut,
    loading,
    signalForRefreshingId,
    setNewSignalForRefreshingId,
    getPeriods,
    onPeriodChange,
    getPreferences,
    updatePreferences,
    updateClockOutOutsideSettings,
    clockOutOutsideSettings,
    setClockOutOutsideSettings,
    autoDeletionEnabled,
    autoDeletionWeeks,
    getAutoDeletionSettings,
    handleToggleAutoDeletion,
    handleAutoDeletionWeeksChange,
  } = useSettingsData({ companyId, getCompanySettings, updateAutoUserDeletion });

  useEffect(() => {
    getPeriods();
    getPreferences();
    getAutoDeletionSettings();
  }, []);

  useEffect(() => {
    if (locations?.length) {
      setClockOutOutsideSettings(locations[0]?.clock_out_settings || null);
    }
  }, [locations]);

  const isOpen = (key: string) => !!openCards[key];
  const setOpen = (key: string) => (expanded: boolean) =>
    setOpenCards(prev => ({ ...prev, [key]: expanded }));

  const sections: Section[] = [
    {
      label: 'Email reports',
      rows: [
        {
          cards: [
            // Scheduled email notifications are a management right of their own,
            // not part of the Time settings bundle.
            ...(can(MANAGEMENT_PERMISSIONS.MANAGE_SCHEDULED_NOTIFICATIONS)
              ? [
                  {
                    key: 'snapshot',
                    search: 'snapshot email clocked in clock out recipients locations days time',
                    node: (
                      <TimeNotifications
                        companyId={companyId}
                        locations={locations}
                        expanded={isOpen('snapshot')}
                        onExpandedChange={setOpen('snapshot')}
                      />
                    ),
                  },
                ]
              : []),
          ],
        },
      ],
    },
    {
      label: 'Hours & pay rules',
      rows: [
        {
          paired: true,
          cards: [
            {
              key: 'autoOut',
              search: 'forgotten clock outs automatic clock out zero hours normal shift pay',
              node: (
                <AutomaticClockOut
                  countAutoClockOut={countAutoClockOut}
                  expanded={isOpen('autoOut')}
                  onExpandedChange={setOpen('autoOut')}
                  updatePreferences={updatePreferences}
                />
              ),
            },
            {
              key: 'away',
              search: 'clock outs away from site work location zero hours time logs governance',
              node: (
                <ZeroHoursForClockOuts
                  clockOutOutsideSettings={clockOutOutsideSettings}
                  expanded={isOpen('away')}
                  onExpandedChange={setOpen('away')}
                  updatePreferences={updateClockOutOutsideSettings}
                />
              ),
            },
          ],
        },
        {
          cards: [
            ...(config.comingSoon
              ? []
              : [
                  {
                    key: 'rules',
                    search: 'bonuses deductions rules paid breaks short shift full work day bonus',
                    node: (
                      <TimeRules
                        companyId={companyId}
                        expanded={isOpen('rules')}
                        onExpandedChange={setOpen('rules')}
                      />
                    ),
                  },
                ]),
          ],
        },
      ],
    },
    {
      label: 'Reporting & team',
      rows: [
        {
          paired: true,
          cards: [
            {
              key: 'period',
              search: 'reporting period weekly fortnightly monthly time logs week start',
              node: (
                <TimeLogsReporting
                  endDay={endDay}
                  settings={settings}
                  expanded={isOpen('period')}
                  onExpandedChange={setOpen('period')}
                  onPeriodChange={onPeriodChange}
                />
              ),
            },
            {
              key: 'dormant',
              search: 'tidy up inactive people dormant team members remove locations weeks no clock in',
              node: (
                <DormantTeamMembers
                  enabled={autoDeletionEnabled}
                  weeks={autoDeletionWeeks}
                  expanded={isOpen('dormant')}
                  onExpandedChange={setOpen('dormant')}
                  onToggle={handleToggleAutoDeletion}
                  onWeeksChange={handleAutoDeletionWeeksChange}
                />
              ),
            },
          ],
        },
      ],
    },
    {
      label: 'Location details & exports',
      rows: [
        {
          cards: [
            {
              key: 'locFields',
              search: 'your own location fields custom location details site manager job number',
              node: (
                <CustomDetailsFieldsModule
                  companyId={companyId}
                  expanded={isOpen('locFields')}
                  onExpandedChange={setOpen('locFields')}
                  setNewSignalForRefreshingId={setNewSignalForRefreshingId}
                />
              ),
            },
          ],
        },
        {
          cards: [
            {
              key: 'exportCols',
              search: 'time log export columns spreadsheet payroll detailed summary',
              node: (
                <ExportingTimeLogsModule
                  companyId={companyId}
                  expanded={isOpen('exportCols')}
                  onExpandedChange={setOpen('exportCols')}
                  signalForRefreshingId={signalForRefreshingId}
                />
              ),
            },
          ],
        },
      ],
    },
  ];

  if (loading) {
    return (
      <Box height="calc(100vh - 200px)">
        <PageLoading />
      </Box>
    );
  }

  return (
    <SettingsPage
      title="Settings"
      description="How Vyce Time behaves for your company — rules, reports and exports."
      sections={sections}
      query={query}
      onQueryChange={setQuery}
    />
  );
};
